import ScrollerNav from "./Scroller/ScrollerNav";
import constants from "./Scroller/Constants";
import { FullscreenGallery } from "./Gallery/Fullscreen";

interface OffsetInfo {
    thumbLeft: number;
    thumbWidth: number;
    paddingLeft: number;
    paddingRight: number;
    scrollLeft: number;
    scrollerWidth: number;
    trackWidth: number;
    trackGap: number;
    tilesOnTrack: number;
}

export class AbstractGallery {
    protected fullScreen: FullscreenGallery;
    protected scrollerHandler: ScrollerNav;
    protected scroller: HTMLElement;

    constructor(public node: HTMLElement) {
        this.node = node;
        if (this.node.dataset.fullscreen !== undefined) {
            this.fullScreen = new FullscreenGallery(this);
        }
    }

    protected init() {
        if (this.node.dataset.fullscreen !== undefined) {
            this.initZoom();
        }
    }

    protected initZoom() {}

    get images(): NodeListOf<HTMLImageElement> {
        return (this.scroller ?? this.node).querySelectorAll<HTMLImageElement>("img");
    }

    get current() {
        return this.scrollerHandler?.current || 0;
    }
}

class Gallery extends AbstractGallery {
    private readonly track: HTMLElement;
    private readonly thumbScroller: HTMLElement;
    private thumbScrollerHandler: ScrollerNav;
    private initSteps = 0;
    private initEventData: CustomEvent;
    private _omitTrackScroll = false;

    constructor(node: HTMLElement) {
        super(node);
        this.scroller = this.node.querySelector<HTMLElement>(constants.SELECTORS.SCROLLER);
        this.track = this.node.querySelector<HTMLElement>(constants.SELECTORS.TRACK);
        this.thumbScroller = this.track.querySelector<HTMLElement>(constants.SELECTORS.SCROLLER);
        this.thumbScroller.addEventListener(constants.EVENTS.SCROLL_NAV_READY, (e: CustomEvent) => {
            this.thumbScrollerHandler = e.detail.handler;
            this.init();
        });
        this.scroller.addEventListener(constants.EVENTS.SCROLL_NAV_READY, (e: CustomEvent) => {
            this.scrollerHandler = e.detail.handler;
            const navHandler = this.handleGalleryNav.bind(this);
            this.scroller.addEventListener(constants.EVENTS.THUMB_START, navHandler);
            this.scroller.addEventListener(constants.EVENTS.NAV_START, navHandler);
            this.scroller.addEventListener(constants.EVENTS.SCROLL_END, navHandler);
            this.initEventData = e;
            this.init();
        });
        this.scroller.addEventListener(constants.EVENTS.SCROLLER_GOTO, (e: CustomEvent) => {
            this.scrollerHandler.goto(e.detail.index, e.detail.behavior);
        });
    }

    init() {
        if (++this.initSteps > 1) {
            this.handleGalleryNav(this.initEventData);
            super.init();
        }
    }

    initZoom() {
        this.scroller.querySelectorAll<HTMLImageElement>("img").forEach((img) => {
            img.addEventListener("mouseup", (e) => this.fullScreen.show(e));
        });
    }

    handleGalleryNav(e: CustomEvent): void {
        const index = Math.max(e.detail.index, 0);
        const offsetInfo = this.currentOffsetInfo(index);

        if (constants.EVENTS.THUMB_START === e.type) {
            this._omitTrackScroll = true;
        }
        if (constants.EVENTS.SCROLL_END === e.type) {
            this._omitTrackScroll = false;
        }

        if (!this._omitTrackScroll && !this.isOnTrack(offsetInfo)) {
            let thumbIndex = index;
            if (this.isOutOfBoundsRight(offsetInfo)) {
                thumbIndex = Math.min(thumbIndex - offsetInfo.tilesOnTrack + 1, this.scrollerHandler.thumbs.length - 1);
            }
            this.thumbScrollerHandler.goto(thumbIndex);
        }
        if (constants.EVENTS.SCROLL_END === e.type || constants.EVENTS.SCROLL_NAV_READY === e.type) {
            const node = (this.isCms ? this.node.parentNode : this.node) as HTMLElement;
            const thumb = this.scrollerHandler.thumbs[index];
            const current = this.scrollerHandler.allThumbs.findIndex((t) => t === thumb);
            node.style.setProperty("--current", `${current}`);
        }
    }

    isOnTrack(offsetInfo: OffsetInfo): boolean {
        const { thumbLeft, paddingLeft, scrollLeft } = offsetInfo;
        return thumbLeft - paddingLeft >= scrollLeft && !this.isOutOfBoundsRight(offsetInfo);
    }

    isOutOfBoundsRight(offsetInfo: OffsetInfo): boolean {
        const { thumbLeft, thumbWidth, paddingLeft, scrollLeft, trackWidth } = offsetInfo;
        return thumbLeft - paddingLeft - scrollLeft > trackWidth - thumbWidth;
    }

    currentOffsetInfo(index: number): OffsetInfo {
        const style = getComputedStyle(this.thumbScroller);
        const paddingLeft = parseInt(style.paddingLeft);
        const paddingRight = parseInt(style.paddingRight);
        const trackWidth = this.thumbScroller.offsetWidth - paddingLeft - paddingRight;
        const thumbWidth = this.scrollerHandler.thumbs[index]?.offsetWidth;
        const trackGap = this.thumbScrollerHandler.gap;
        return {
            thumbLeft: this.scrollerHandler.thumbs[index]?.offsetLeft,
            thumbWidth,
            scrollLeft: this.thumbScroller.scrollLeft,
            scrollerWidth: this.thumbScroller.offsetWidth,
            paddingLeft,
            paddingRight,
            trackWidth,
            trackGap,
            tilesOnTrack: Math.floor(trackWidth / (thumbWidth + trackGap))
        };
    }

    get isCms() {
        const node = this.node.parentNode as HTMLElement;
        return node.classList.contains(constants.CLASSES.CMS_GALLERY);
    }
}

class SingleGallery extends AbstractGallery {
    constructor(node: HTMLElement) {
        super(node);
        this.init();
    }

    initZoom() {
        this.node.querySelector<HTMLImageElement>("img").addEventListener("mouseup", (e) => this.fullScreen.show(e));
    }
}

const galleries = document.querySelectorAll("section.gallery") as NodeListOf<HTMLElement>;
if (galleries.length) {
    galleries.forEach((gallery: HTMLElement) => {
        new Gallery(gallery);
    });
}

const singleGalleries = document.querySelectorAll("section.gallery-single") as NodeListOf<HTMLElement>;
if (singleGalleries.length) {
    singleGalleries.forEach((gallery: HTMLElement) => {
        new SingleGallery(gallery);
    });
}
